import { useEffect } from "react";
import {
  AlignItems,
  Box,
  Button,
  ButtonVariant,
  ButtonSize,
  ColorPreset,
  Form,
  H2,
  JustifyContent,
  P,
  Space,
  Banner,
  BannerVariant,
  BannerStatus,
  ButtonGroup,
  TagSize,
  Tag,
  ColorScheme,
  TagColor,
  TagVariant,
  BannerLeftAccessoryType,
  PlainLink,
  FontWeight,
  TextAlign,
  HoverEffect,
} from "@gocardless/flux-react";
import { useForm } from "react-hook-form";
import { Trans } from "@lingui/macro";

import { SignUpAccountType } from "../../SignUp";
import { EmailField } from "../../form-field-components/EmailField";

import { getRouteURL, useRouterQuery, Route } from "src/common/routing";
import { useSegment } from "src/technical-integrations/segment/useSegment";
import {
  invalidGoCardlessEmailSignUp,
  VALID_EMAIL_REGEX,
} from "src/utils/inputValidation";
import { SandboxTag } from "src/common/SandboxTag";
import {
  isCountryCodeSupported,
  isSandboxEnvironment,
} from "src/common/consts";
import { CountryCodeSupported } from "src/common/country";
import { ToTranslate } from "src/components/i18n";
import { TrackingEvent } from "src/common/trackingEvents";
import { useReferralAmountByGeo } from "src/components/routes/Refer/hooks/useReferralAmount";
import { useAccessToken } from "src/common/authorisation";
import { FSB_MEMBER_DISCOUNT_TERMS_LINK } from "src/components/routes/SignUp/consts";
import {
  ReferralEmailSource,
  ReferralEmailVariation,
} from "src/common/trackingEventAttributes";
import { HEADER_HEIGHT_MD_LG } from "src/components/layout/core/Headers/constants";

export interface SignUpGetStartedFormProps {
  country: CountryCodeSupported;
  accountType: SignUpAccountType;
  referralGeo: string | null;
  referralCode: string | null;
  referralCodeName?: string | null;
  isPaymentProvider?: boolean;
  hasFsbMemberDiscount?: boolean;
  referralSource?: ReferralEmailSource | null;
  referralVariation?: ReferralEmailVariation | null;
  submit: (email: string) => void;
}

export const SignUpGetStartedForm: React.FC<SignUpGetStartedFormProps> = ({
  accountType,
  referralGeo,
  referralCode,
  referralCodeName,
  referralSource,
  referralVariation,
  isPaymentProvider,
  hasFsbMemberDiscount,
  submit,
}) => {
  const [accessToken] = useAccessToken();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
    },
    mode: "onSubmit",
    shouldUnregister: true,
  });

  const { sendEvent } = useSegment();

  const onSubmit = (email: string) => {
    submit(email);
  };

  const isSalesServed = accountType === SignUpAccountType.SALES_SERVED;

  // referral_code is ignored if in sandbox or not in an allowed geo
  const hasReferralCode = !!(
    !isSandboxEnvironment &&
    referralCode &&
    referralGeo &&
    isCountryCodeSupported(referralGeo) &&
    referralGeo !== CountryCodeSupported.DE
  );

  // referral_code_name is ignored if referral_code is not also set
  const hasReferralCodeName = !!(hasReferralCode && referralCodeName);
  const promo90DaysOn = useRouterQuery("promo").item === "no-fees-90-days";
  const referralAmount = useReferralAmountByGeo(referralGeo);

  const SignUpGetStartedHeadline = (() => {
    if (isPaymentProvider)
      return (
        <Trans id="sign-up.get-started-marketing.form.heading-embed">
          Get started with GoCardless Embed
        </Trans>
      );

    if (isSandboxEnvironment)
      return (
        <Trans id="sign-up.get-started-marketing.form.heading-sandbox">
          Get started with a sandbox account
        </Trans>
      );

    if (hasFsbMemberDiscount)
      return <Trans>Sign up today and get a 25% discount for 12 months</Trans>;

    if (promo90DaysOn)
      // update form heading when the user comes through from the 90 day promo banner or CTA on the website
      return (
        <Trans id="sign-up.get-started-marketing-withpromo.form.heading">
          Join today and pay no transaction fees for 90 days
        </Trans>
      );

    if (isSalesServed)
      return (
        <Trans id="sign-up.get-started-marketing.form.heading-sales-served">
          Get started with GoCardless
        </Trans>
      );

    return (
      <Trans id="sign-up.get-started-marketing.form.heading">
        Get started for free with GoCardless
      </Trans>
    );
  })();

  const fsbDiscountTag = (
    <Tag
      variant={TagVariant.Solid}
      color={TagColor.Info}
      colorScheme={ColorScheme.OnDark}
      size={TagSize.Lg}
    >
      <Trans>FSB member discount</Trans>
    </Tag>
  );

  const fsbTermsAndConditions = (
    <PlainLink
      href={FSB_MEMBER_DISCOUNT_TERMS_LINK}
      effect={HoverEffect.TextDecoration}
      textDecoration="underline"
      textAlign={TextAlign.Center}
      weight={FontWeight.SemiBold}
    >
      <Trans>T&Cs apply</Trans>
    </PlainLink>
  );

  useEffect(() => {
    sendEvent(TrackingEvent.SIGNUP_EMAIL_PAGE_VIEWED, {
      has_referral_code: hasReferralCode,
      partner_name: null,
      form_type: hasReferralCode ? "referral" : "self-serve",
      ...(hasReferralCode && {
        referral_source: referralSource,
        ...(referralVariation && { referral_variation: referralVariation }),
        ...(referralCodeName && { referrer: referralCodeName }),
        referralGeo: referralGeo,
      }),
    });
    // TODO: Fix exhaustive dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSignedInOnReferralPage = !!accessToken && hasReferralCode;

  return (
    <Box
      layout="flex"
      flexDirection="column"
      justifyContent={[JustifyContent.Start, null, null, JustifyContent.Center]}
      height={[null, null, null, `calc(100% - ${HEADER_HEIGHT_MD_LG})`]} // Visually center form when available
    >
      <SandboxTag />

      {hasReferralCode ? (
        <Box alignSelf={AlignItems.FlexStart} spaceBelow={0.75}>
          <Tag
            size={TagSize.Lg}
            colorScheme={ColorScheme.OnDark}
            color={TagColor.Primary}
          >
            <ToTranslate id="refer.sign-up.tag">
              Limited time invitation
            </ToTranslate>
          </Tag>
        </Box>
      ) : null}

      {hasFsbMemberDiscount && <Box spaceBelow={1}>{fsbDiscountTag}</Box>}
      <H2
        color={ColorPreset.TextOnLight_01}
        spaceBelow={hasReferralCode ? 0.75 : 2}
        size={hasFsbMemberDiscount ? [8, null, 5, 7] : [8, null, 5, 6]}
      >
        {hasReferralCode ? (
          hasReferralCodeName ? (
            <ToTranslate id="refer.sign-up.referral-code-name-heading">
              {referralCodeName} invited you to join GoCardless with a{" "}
              {referralAmount.incentive} reward
            </ToTranslate>
          ) : (
            <ToTranslate id="refer.sign-up.referral-code-heading">
              Join GoCardless with a {referralAmount.incentive} reward
            </ToTranslate>
          )
        ) : (
          SignUpGetStartedHeadline
        )}
      </H2>
      <Form
        noValidate
        data-reading-optimized
        onSubmit={handleSubmit((data) => onSubmit(data.email))}
      >
        {isSalesServed && (
          <>
            <Banner
              variant={BannerVariant.Light}
              leftAccessory={{
                type: BannerLeftAccessoryType.Status,
                status: BannerStatus.Success,
              }}
            >
              <Trans id="gocardless-team-link-banner">
                Your business has been sent this sign up link by a member of the
                GoCardless team.
              </Trans>
            </Banner>
            <Space v={2} />
          </>
        )}

        {hasReferralCode && (
          <>
            <P color={ColorPreset.TextOnLight_02}>
              <Trans id="refer.sign-up.intro">
                There&apos;s no transaction fees for 90 days and we&apos;ll also
                give you {referralAmount.incentive} as a reward for collecting
                at least {referralAmount.threshold}.
              </Trans>
            </P>
            <Space v={2} />
          </>
        )}

        <EmailField
          {...register("email", {
            required: true,
            pattern: VALID_EMAIL_REGEX,
            validate: (value) => !invalidGoCardlessEmailSignUp(value),
          })}
          error={errors.email}
        />
        <Space v={1.5} />
        <Box
          width="100%"
          layout="flex"
          flexDirection={["column", "row"]}
          alignItems={[null, AlignItems.Center]}
        >
          <ButtonGroup>
            <Button
              type="submit"
              variant={ButtonVariant.PrimaryOnLight}
              size={ButtonSize.Md}
              disabled={isSignedInOnReferralPage}
            >
              {hasReferralCode ? (
                <ToTranslate id="refer.sign-up.cta.get-started-free">
                  Get started free
                </ToTranslate>
              ) : (
                <Trans id="Get started">Get started</Trans>
              )}
            </Button>
            {hasReferralCode && (
              <>
                <Space layout="inline" h={1.5} />
                <PlainLink
                  href="https://gocardless.com/g/referral-scheme-v2/"
                  effect={HoverEffect.TextDecoration}
                  textAlign={TextAlign.Center}
                  weight={FontWeight.SemiBold}
                  target="_blank"
                >
                  Terms
                </PlainLink>
              </>
            )}
            {hasFsbMemberDiscount && (
              <>
                <Space layout="inline" h={1.5} />
                {fsbTermsAndConditions}
              </>
            )}
          </ButtonGroup>
        </Box>
        {!hasReferralCode && (
          <>
            <Space v={3} />
            <P
              color={ColorPreset.TextOnLight_01}
              size={3}
              spaceBelow={[3, null, null, 0]}
            >
              <Trans id="already-have-account">Already have an account?</Trans>{" "}
              <PlainLink
                href={getRouteURL({
                  route: Route.SignIn,
                })}
                onClick={() => sendEvent(TrackingEvent.SIGNUP_LOGIN_CLICKED)}
                effect={HoverEffect.TextDecoration}
                textDecoration="underline"
                weight={FontWeight.SemiBold}
                textAlign={TextAlign.Center}
              >
                <Trans id="Sign in">Sign in</Trans>
              </PlainLink>
            </P>
          </>
        )}
      </Form>
    </Box>
  );
};
